import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { Tabs, Tab, Paper } from "@mui/material";
import Button from "../../common/components/Button";
import { CellStepManagementOperations } from "../../CellStepManagement/duck/index";
import URL_REGEX from "../../CellStepManagement/duck/constants";

import StepTypeExternal from "./StepTypeExternal";
import StepTypeEmbedded from "./StepTypeEmbedded";
import StepTypeInstruction from "./StepTypeInstruction";
import StepTypeCachePage from "./StepTypeCachePage";
import StepTypeStim from "./StepTypeStim";
import StepTypeQuestion from "./StepTypeQuestion";
import StepTypeCommand from "./StepTypeCommand";
// import StepTypeValidation from "./StepTypeValidation";
import StepTypeDistraction from "./StepTypeDistraction";
import StepTypeReusableCachePage from "./StepTypeReusableCachePage";

const toCategoryLabel = (raw) => {
  const v = (raw || "").toLowerCase();
  if (v === "control") return "Control";
  if (v === "stim") return "Stim";
  if (v === "question") return "Question";
  return "";
};

const sortFuncs = {
  instruction: (data, term, loading) => sortFuncs.filter(data, term, "instruction_name", loading),
  default: (data) => data,
  stim: (data, term) => sortFuncs.cache(data, term),
  distraction: (data, term) => sortFuncs.cache(data, term),
  cache: (data, term, loading) => sortFuncs.filter(data, term, "name", loading),
  reusable_cache: (data, term, loading) => sortFuncs.cache(data, term, "name", loading),
  validation: (data) => data,
  calibration: (data) => data,
  question: (data, term, loading) => sortFuncs.cache(data, term),
  external: (data, term, loading) => sortFuncs.cache(data, term),
  embedded: (data, term) => sortFuncs.cache(data, term),
  command: (data) => data,
  filter: (data, term, obj) => {
    try {
      return data.filter((val) => val[obj].toUpperCase().includes(term.toUpperCase()));
    } catch (e) {
      return data;
    }
  },

  cache: (...args) => {
    const [data] = args;
    return data || [];
  },
};

function AddStepModal(props) {
  const [value, setValue] = useState("Please Select");
  const [activeTab, setActiveTab] = useState(0);
  const [stepType, setStepType] = useState("");
  const [error, setError] = useState(false);
  const [param, setParam] = useState("");
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [stepCategory, setStepCategory] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [studyId, setStudyId] = useState(undefined);
  const [newItemByStepType, setNewItemByStepType] = useState({});
  const [applyToAllCells, setApplyToAllCells] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageSrc, setImageSrc] = useState("");

  //route 
  const routeStudyId = props.match?.params?.studyId;
  const routeColumnId = props.match?.params?.columnId;
  const routeCategoryRaw = props.match?.params?.category;

  //used by steps
  const effectiveStudyId = routeStudyId || localStorage.getItem("studyId");
  const effectiveCategory = toCategoryLabel(routeCategoryRaw);
  const effectiveColumnId = routeColumnId || props.id;
  const getTabOptions = useCallback((category) => {
    // if (category === "Control") return ["Validation", "Instruction", "Command"];
    if (category === "Control") return ["Instruction", "Command"];
    if (category === "Stim") return ["Reusable Cache Page", "Cache Page", "Stim", "Embedded", "Distraction"];
    if (category === "Question") return ["Question", "External"];
    return [];
  }, []);

  //page every time url category study changes
  useEffect(() => {
    if (!effectiveCategory) return;

    const options = getTabOptions(effectiveCategory);
    const firstStepType = options[0] || "";

    setStepCategory(effectiveCategory);
    setActiveTab(0);
    setStepType(firstStepType);
    setValue(firstStepType);

    setSubmitted(false);
    setStudyId(effectiveStudyId);

    setHasSearched(false);
    setCurrentPage(1);
    setRowsPerPage(25);

    props.clearPrimarySelection?.();

    // const noFetchTypes = ["Validation", "Calibration"];
    const noFetchTypes = ["Calibration"];
    if (firstStepType && !noFetchTypes.includes(firstStepType)) {
      props.resetStateToLoading?.();
      props.onSelectItem?.(firstStepType, effectiveStudyId);
    } else if (firstStepType) {
      props.setStepType?.(firstStepType);
    }
  }, [effectiveCategory, effectiveStudyId, getTabOptions]);

  const onChange = (e) => setUrl(e.target.value);
  const onParamChange = (e) => setParam(e.target.value);
  const onNameChange = (e) => setName(e.target.value);
  const validateUrl = (u) => URL_REGEX.test(u);

  //page close should go back
  const resetForm = () => {
    setStepType("");
    setValue("Please Select");
    setUrl("");
    setError(false);
    setSubmitted(true);
    setParam("");
    setName("");
    setActiveTab(0);
    setNewItemByStepType({});
    setHasSearched(false);

    props.history.goBack();
  };

  const toggleItem = () => {
    setNewItemByStepType((prev) => ({
      ...prev,
      [stepType]: !prev[stepType],
    }));
  };

  const handleClick = async (id, val, sid, cacheId, options = {}) => {
    const { closeAfter = true } = options;
    const safeId = id || effectiveColumnId;
    const safeStudyId = sid || effectiveStudyId;

    if (!safeId || !safeStudyId) {
      console.error("Missing ids:", { id, sid, effectiveColumnId, effectiveStudyId });
      alert("Missing study/cell id. Please refresh and try again.");
      return;
    }

    let valueToSend = val;

    switch (stepType) {
      // case "Validation":
      case "Calibration":
        props.addTask(id, val, sid, stepType, null, null, false, applyToAllCells);
        resetForm();
        return;

      case "Question":
        valueToSend = `question:${id}`;
        props.addTask(safeId, valueToSend, safeStudyId, stepType, cacheId, null, false, applyToAllCells);
        if (closeAfter) resetForm();
        return;

      case "Embedded":
      case "External":
        if (newItemByStepType[stepType]) {
          if (!validateUrl(url)) {
            setError(true);
            return;
          }
          valueToSend = url;

          props.addTask(safeId, valueToSend, safeStudyId, stepType, param, name, true, applyToAllCells);
          if (closeAfter) resetForm();
          return;
        }

        valueToSend = `${stepType.toLowerCase()}:${id}`;
        props.addTask(safeId, valueToSend, safeStudyId, stepType, cacheId, null, false, applyToAllCells);
        if (closeAfter) resetForm();
        return;

      case "Instruction":
        if (newItemByStepType[stepType]) {
          if (!name || !props.template || !props.contents?.default) {
            setError(true);
            return;
          }

          props.addTask(
            safeId,
            props.contents.default,
            safeStudyId,
            stepType,
            imageSrc,
            name,
            true,
            applyToAllCells,
            props.template
          );
          if (closeAfter) resetForm();
          return;
        }

        // adding missing handling for existing instruction steps to prevent fall-through
        valueToSend = `instruction:${id}`;
        props.addTask(safeId, valueToSend, safeStudyId, stepType, cacheId, null, false, applyToAllCells);
        if (closeAfter) resetForm();
        return;

      case "Reusable Cache Page": {
        const numericCacheId = parseInt(cacheId, 10);

        if (!numericCacheId || Number.isNaN(numericCacheId)) {
          console.error("Reusable Cache Page missing cacheId:", cacheId);
          alert("Missing cache page id. Please try again.");
          return;
        }
        const response = await props.addTask(
          safeId,
          `cache:${numericCacheId}`,
          safeStudyId,
          stepType,
          numericCacheId,
          null,
          false
        );
        if (closeAfter) resetForm();
        return response;
      }

      case "Cache Page": {
        const numericCacheId = parseInt(cacheId, 10);

        if (!numericCacheId || Number.isNaN(numericCacheId)) {
          console.error("Cache Page missing cacheId:", cacheId);
          alert("Missing cache page id. Please try again.");
          return;
        }

        props.addTask(
          safeId,
          `cache:${numericCacheId}`,
          safeStudyId,
          stepType,
          numericCacheId,
          null,
          false,
          applyToAllCells
        );

        if (closeAfter) resetForm();
        return;
      }

      case "Stim": {
        const numericStimId = parseInt(cacheId, 10);

        if (!numericStimId || Number.isNaN(numericStimId)) {
          console.error("Stim missing stimId:", cacheId);
          alert("Missing stim id. Please try again.");
          return;
        }

        props.addTask(
          safeId,
          `stim:${numericStimId}`,
          safeStudyId,
          stepType,
          numericStimId,
          null,
          false,
          applyToAllCells
        );

        if (closeAfter) resetForm();
        return;
      }

      case "Distraction": {
        const numericStimId = parseInt(cacheId, 10);

        if (!numericStimId || Number.isNaN(numericStimId)) {
          console.error("Distraction missing stimId:", cacheId);
          alert("Missing distraction stim id. Please try again.");
          return;
        }

        props.addTask(
          safeId,
          `stim:${numericStimId}`,
          safeStudyId,
          stepType,
          numericStimId,
          null,
          false,
          applyToAllCells
        );

        if (closeAfter) resetForm();
        return;
      }


      case "Command": {
        const numericCommandId = parseInt(cacheId, 10);

        if (!numericCommandId || Number.isNaN(numericCommandId)) {
          console.error("Command missing commandId:", cacheId);
          alert("Missing command id. Please try again.");
          return;
        }

        props.addTask(
          safeId,
          `command:${numericCommandId}`,
          safeStudyId,
          stepType,
          numericCommandId,
          null,
          false,
          applyToAllCells
        );

        if (closeAfter) resetForm();
        return;
      }


      default:
        valueToSend = `${stepType.toLowerCase().replace(" ", "")}:${id}`;
        props.addTask(id, valueToSend, sid, stepType, cacheId, null, false, applyToAllCells);
        resetForm();
        return;
    }
  };

  const handleStimUploadComplete = async (stimId) => {
    try {
      if (!stimId) return alert("Upload failed: Stim ID missing.");

      await props.addTask(
        props.id,
        `stim:${stimId}`,
        studyId,
        "Stim",
        stimId,
        null,
        true,
        applyToAllCells
      );

      alert("Stim uploaded and added successfully!");
      resetForm();
    } catch (e) {
      console.error("Failed to add new stim to step:", e);
      alert("Failed to link stim to step.");
    }
  };

  const onCachePageSumbit = (cacheUrl, cacheName) => {
    props.onCachePageSumbit(cacheUrl, cacheName, studyId);
  };

  // const showSubmitButton = stepType === "Calibration" || stepType === "Validation";
  const showSubmitButton = stepType === "Calibration";

  const stepProps = {
    id: effectiveColumnId,
    studyId,
    stepType,
    data: props.dataFromPrimarySelection,
    loading: props.loading,
    rowsPerPage,
    currentPage,
    cells: props.cells,
    onPageChange: (newPage) => {
      setCurrentPage(newPage);
      if (stepType !== "Reusable Cache Page") {
        props.onSelectItem(stepType, effectiveStudyId, props.searchTerm || "", rowsPerPage, newPage);
      }
    },
    onRowsPerPageChange: (newRows) => {
      setRowsPerPage(newRows);
      setCurrentPage(1);
      if (stepType !== "Reusable Cache Page") {
        props.onSelectItem(stepType, effectiveStudyId, props.searchTerm || "", newRows, 1);
      }
    },
    onSearch: props.onSearch,
    setHasSearched,
    hasSearched,
    searchTerm: props.searchTerm,
    resetStateToLoading: props.resetStateToLoading,
    onSelectItem: props.onSelectItem,
    newItem: newItemByStepType?.[stepType],
    toggleItem,
    url,
    param,
    name,
    onChange,
    onParamChange,
    onNameChange,
    handleClick,
    onDone: resetForm,
    openCacheModal: props.openCacheModal,
    closeCacheModal: props.closeCacheModal,
    cachePageModalOpen: props.cachePageModalOpen,
    onCachePageSubmit: onCachePageSumbit,
    handleStimUploadComplete,
    resultCount: props.stepPagination?.result_count,
    onTemplateChange: (e) => setParam(e.target.value),
    imageSrc,
    onImageChange: (e) => setImageSrc(e.target.value),
    clearPrimarySelection: props.clearPrimarySelection,
    getStepData: props.getStepData
  };

  return (
    <div
      className="add-step-page"
      style={{
        width: "100%",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        padding: "16px 24px 24px 24px",
        boxSizing: "border-box",
      }}
    >
      {/*tabs */}
      <Paper
        elevation={1}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#fff",
          marginBottom: 24,
          padding: "6px 8px",
          borderBottom: "1px solid rgba(0,0,0,0.03)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "40px 1fr 40px",
            alignItems: "center",
            columnGap: 8,
          }}
        >
          <div />
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              const selected = getTabOptions(stepCategory)[newValue];
              const sid = effectiveStudyId;

              setActiveTab(newValue);
              setStepType(selected);
              setValue(selected);
              setSubmitted(false);
              setStudyId(sid);
              setHasSearched(false);
              setCurrentPage(1);
              setRowsPerPage(25);

              props.clearPrimarySelection?.();

              const noFetchTypes = ["Validation", "Calibration"];
              if (noFetchTypes.includes(selected)) {
                props.setStepType?.(selected);
              } else {
                props.resetStateToLoading?.();
                props.onSelectItem?.(selected, sid);
              }
            }}
            indicatorColor="primary"
            textColor="primary"
            centered
            variant="fullWidth"
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 48,
                fontWeight: 600,
              },
            }}
          >
            {getTabOptions(stepCategory).map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>

          <button
            onClick={resetForm}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: 22,
              lineHeight: 1,
              padding: 6,
              color: "#444",
              justifySelf: "end",
              marginRight: -50,
              marginTop: -40,
            }}
          >
            ×
          </button>
        </div>
      </Paper>

      {/*step  */}
      <div style={{ width: "100%" }}>
        {{
          External: <StepTypeExternal {...stepProps} />,
          Embedded: <StepTypeEmbedded {...stepProps} />,
          Instruction: <StepTypeInstruction {...stepProps} />,
          "Cache Page": <StepTypeCachePage {...stepProps} />,
          "Reusable Cache Page": <StepTypeReusableCachePage {...stepProps} />,
          Stim: <StepTypeStim {...stepProps} />,
          Distraction: <StepTypeDistraction {...stepProps} />,
          Question: <StepTypeQuestion {...stepProps} />,
          Command: <StepTypeCommand {...stepProps} cells={props.cells} />,
          // Validation: <StepTypeValidation />,
        }[stepType] || null}

        {error && (
          <div className="csm__error-message active" style={{ marginTop: 16 }}>
            {stepType === "Instruction"
              ? "Please fill in Name, Template, and Content"
              : "Please enter a valid URL"}
          </div>
        )}

        {showSubmitButton && (
          <div style={{ marginTop: 16 }}>
            <Button
              handleClick={() => handleClick(effectiveColumnId, value, effectiveStudyId)}
              text={`Add ${stepType} step`}
              buttonClass="button-primary show-study__button"
            />

          </div>
        )}
      </div>
    </div>
  );


}

//redux
const mapDispatchToProps = (dispatch) => ({
  resetStateToLoading: () => dispatch(CellStepManagementOperations.resetStateToLoading()),

  onSelectItem: (item, id, searchTerm, resultsPerPage, pageNum, approved, extraFilters) =>
    dispatch(
      CellStepManagementOperations.onSelectItem(
        item,
        id,
        searchTerm,
        resultsPerPage,
        pageNum,
        approved,
        extraFilters
      )
    ),



  addTask: (id, value, studyId, item, cacheId, name, newItem, applyToAllCells, template) =>
    dispatch(CellStepManagementOperations.addTask(id, value, studyId, item, cacheId, name, newItem, applyToAllCells, template)),
  onSearch: (value) => dispatch(CellStepManagementOperations.onSearch(value)),
  closeCacheModal: () => dispatch(CellStepManagementOperations.closeCacheModal()),
  openCacheModal: () => dispatch(CellStepManagementOperations.openCacheModal()),
  onCachePageSumbit: (url, name, studyId) =>
    dispatch(CellStepManagementOperations.onCachePageSumbit(url, name, studyId)),//here
  clearPrimarySelection: () => dispatch(CellStepManagementOperations.clearPrimarySelection()),
  setStepType: (type) => dispatch(CellStepManagementOperations.setStepCategory(type)),
  getStepData: (id) => dispatch(CellStepManagementOperations.getStepData(id)),

});

const mapStateToProps = (state) => {
  const stepType = state.cellStepManagement.stepType;

  return {
    id: state.cellStepManagement.id,

    loading: stepType === "Command" ? false : state.cellStepManagement.dropdownLoading,

    dataFromPrimarySelection:
      stepType === "Command"
        ? state.cellStepManagement.dataFromPrimarySelection
        : sortFuncs[stepType?.toLowerCase()]?.(
          state.cellStepManagement.dataFromPrimarySelection,
          state.cellStepManagement.searchTerm
        ) || [],

    cachePageModalOpen: state.cellStepManagement.cachePageModalOpen,
    stepPagination: state.cellStepManagement.paginationData,
    searchTerm: state.cellStepManagement.searchTerm,

    contents: state.cellStepManagement.contents,
    template: state.cellStepManagement.param,

    cells: Object.values(state.cellStepManagement.cellData.columns || {}),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddStepModal);
